import { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';
import { MdOutlineDirectionsBike } from 'react-icons/md';

function AdminBikes({ eventId, config, primaryColor, secondaryColor, backgroundColor, reservations: externalReservations, activeTab: externalActiveTab, onTabChange, onNoteSaved }) {
  const [seats, setSeats] = useState([]);
  const [reservations, setReservations] = useState(externalReservations || []);
  const [loading, setLoading] = useState(!externalReservations);
  const [internalTab, setInternalTab] = useState(config?.sessions?.[0]?.id || '');
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [editingRowNote, setEditingRowNote] = useState(null);
  const [savingNote, setSavingNote] = useState(false);
  const [localConfig, setLocalConfig] = useState(config);

  // Solo sincronizar localConfig si el config del padre trae datos nuevos que no son notas
  // (evitar pisar notas recién guardadas localmente)
  useEffect(() => {
    if (!config) return;
    setLocalConfig(prev => {
      // Mantener rowNotes del estado local (recién guardadas) sobre las del prop
      const merged = {
        ...config,
        sessions: (config.sessions || []).map((s, i) => {
          const localSession = prev?.sessions?.[i];
          return {
            ...s,
            rowNotes: localSession?.rowNotes ?? s.rowNotes
          };
        })
      };
      return merged;
    });
  }, [config]); // eslint-disable-line react-hooks/exhaustive-deps

  // Si el padre controla el tab, usar el suyo; si no, usar el interno
  const activeTab = externalActiveTab !== undefined ? externalActiveTab : internalTab;
  const setActiveTab = (id) => {
    if (onTabChange) onTabChange(id);
    else setInternalTab(id);
  };

  const sessions = localConfig?.sessions || [];

  useEffect(() => {
    if (!eventId) return;
    fetchSeats();
    if (!externalReservations) fetchReservations();
  }, [eventId]);

  // Sync cuando el padre actualiza las reservas
  useEffect(() => {
    if (externalReservations) setReservations(externalReservations);
  }, [externalReservations]);

  // Realtime propio — actualiza el mapa instantáneamente
  useEffect(() => {
    if (!eventId) return;
    const channel = supabase
      .channel(`admin-bikes-${eventId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reservations', filter: `event_id=eq.${eventId}` },
        () => fetchReservations()
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [eventId]);

  const fetchSeats = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('seats').select('*').eq('event_id', eventId)
        .order('row_number').order('seat_number');
      setSeats(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchReservations = async () => {
    try {
      const { data } = await supabase.from('reservations').select('*').eq('event_id', eventId);
      setReservations(data || []);
    } catch (e) { console.error(e); }
  };

  const getSeatStatus = (seat) => {
    const hasOld = reservations.some(r => r.session_id === eventId);
    const res = hasOld
      ? reservations.find(r => r.seat_id === seat.id)
      : reservations.find(r => r.seat_id === seat.id && r.session_id === activeTab);
    return res ? res.status : 'disponible';
  };

  const getSeatReservation = (seat) => {
    const hasOld = reservations.some(r => r.session_id === eventId);
    return hasOld
      ? reservations.find(r => r.seat_id === seat.id) || null
      : reservations.find(r => r.seat_id === seat.id && r.session_id === activeTab) || null;
  };

  // Redistribuir por rowConfiguration del config (igual que el público ve las bicis).
  // Ignoramos row_number de BD porque puede estar desactualizado si se editó el evento.
  const rowConfig = localConfig?.sessions?.[0]?.rowConfiguration;
  const seatsByRow = (() => {
    if (rowConfig?.length > 0 && seats.length > 0) {
      const sorted = [...seats].sort((a, b) => a.seat_number - b.seat_number);
      const result = {};
      let idx = 0;
      rowConfig.forEach((count, i) => {
        const n = parseInt(count) || 0;
        result[i + 1] = [];
        for (let j = 0; j < n && idx < sorted.length; j++, idx++) {
          result[i + 1].push(sorted[idx]);
        }
      });
      return result;
    }
    // Fallback si no hay rowConfiguration
    return seats.reduce((acc, seat) => {
      const row = seat.row_number;
      if (!acc[row]) acc[row] = [];
      acc[row].push(seat);
      return acc;
    }, {});
  })();

  // Detecta si hay reservas con formato viejo (session_id == event_id)
  const hasOldFormat = reservations.some(r => r.session_id === eventId);
  const sessionReservations = hasOldFormat
    ? reservations
    : reservations.filter(r => r.session_id === activeTab);

  const totalSelectable = seats.filter(s => s.is_selectable).length;
  const occupiedCount = sessionReservations.filter(r => r.status === 'ocupada').length;
  const pendingCount = sessionReservations.filter(r => r.status === 'reservada').length;
  const availableCount = totalSelectable - occupiedCount - pendingCount;

  // Obtiene el rowIndex (0-based) de un asiento según seatsByRow
  const getRowIndexOfSeat = (seat) => {
    const entries = Object.entries(seatsByRow).sort((a, b) => Number(a[0]) - Number(b[0]));
    for (let i = 0; i < entries.length; i++) {
      if (entries[i][1].some(s => s.id === seat.id)) return i;
    }
    return (seat.row_number || 1) - 1;
  };

  const handleSaveRowNote = async (rowIndex, note) => {
    setSavingNote(true);
    try {
      const sessionIndex = localConfig.sessions.findIndex(s => s.id === activeTab);
      if (sessionIndex === -1) return;
      const newSessions = localConfig.sessions.map((s, i) => {
        if (i !== sessionIndex) return s;
        const currentNotes = s.rowNotes || s.rowConfiguration.map(() => '');
        const newNotes = currentNotes.map((v, ri) => ri === rowIndex ? note : v);
        return { ...s, rowNotes: newNotes };
      });
      // Limpiar base64 del config antes de guardar para evitar payload pesado
      const newConfig = {
        ...localConfig,
        sessions: newSessions.map(s => ({
          ...s,
          image: s.image && !s.image.startsWith('data:') ? s.image : ''
        }))
      };
      await supabase.from('events').update({ config: newConfig }).eq('id', eventId);
      setLocalConfig(newConfig);
      setEditingRowNote(null);
    } catch (e) {
      console.error('Error guardando nota:', e);
    } finally {
      setSavingNote(false);
    }
  };

  const bikeColor = (status, isSelectable) => {
    if (!isSelectable) return '#1e2a2e';
    if (status === 'ocupada') return '#ef4444';
    if (status === 'reservada') return '#fbbf24';
    return '#4ade80';
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '40px', color: '#4a5568', fontSize: '13px' }}>
      Cargando mapa...
    </div>
  );

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* Tabs de sesión — solo si hay más de una Y el padre no los controla */}
      {sessions.length > 1 && externalActiveTab === undefined && (
        <div style={{
          display: 'flex', gap: '4px', marginBottom: '14px',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '10px', padding: '4px', overflowX: 'auto'
        }}>
          {sessions.map(s => (
            <button
              key={s.id}
              onClick={() => { setActiveTab(s.id); setSelectedSeat(null); }}
              style={{
                flex: 1, minWidth: '90px', padding: '8px 10px',
                borderRadius: '8px', border: 'none', fontSize: '12px',
                fontWeight: activeTab === s.id ? '700' : '500',
                cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                background: activeTab === s.id ? `${primaryColor}22` : 'transparent',
                color: activeTab === s.id ? primaryColor : '#4a5568',
                outline: activeTab === s.id ? `1px solid ${primaryColor}40` : '1px solid transparent'
              }}
            >
              {s.event_name || s.id}
            </button>
          ))}
        </div>
      )}

      {/* Stats compactas */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
        {[
          { label: 'Disp.', count: availableCount, color: '#4ade80' },
          { label: 'Pend.', count: pendingCount, color: '#fbbf24' },
          { label: 'Ocup.', count: occupiedCount, color: '#ef4444' }
        ].map(s => (
          <div key={s.label} style={{
            flex: 1, textAlign: 'center', padding: '8px 4px',
            borderRadius: '8px',
            border: `1px solid ${s.color}25`,
            background: 'transparent'
          }}>
            <p style={{ color: s.color, fontSize: '18px', fontWeight: '800', margin: 0, lineHeight: 1 }}>{s.count}</p>
            <p style={{ color: '#4a5568', fontSize: '10px', fontWeight: '600', margin: '2px 0 0 0' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Leyenda */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '12px', justifyContent: 'center' }}>
        {[
          { color: '#4ade80', label: 'Disponible' },
          { color: '#fbbf24', label: 'Pendiente' },
          { color: '#ef4444', label: 'Ocupado' }
        ].map(l => (
          <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <MdOutlineDirectionsBike size={14} color={l.color} />
            <span style={{ color: '#4a5568', fontSize: '11px' }}>{l.label}</span>
          </div>
        ))}
      </div>

      {/* Mapa — misma estructura que SeatMap */}
      <div style={{
        border: `1px solid rgba(255,255,255,0.06)`,
        borderRadius: '14px', padding: '10px 6px', marginBottom: '12px',
        overflowX: 'auto'
      }}>
        {(() => {
          const row1Seats = seatsByRow['1'] || [];
          const mid = Math.ceil(row1Seats.length / 2);
          const leftSeats = row1Seats.slice(0, mid);
          const rightSeats = row1Seats.slice(mid);

          const renderBike = (seat) => {
            const status = getSeatStatus(seat);
            const res = getSeatReservation(seat);
            const color = bikeColor(status, seat.is_selectable);
            const isSelected = selectedSeat?.id === seat.id;
            return (
              <div
                key={seat.id}
                onClick={() => seat.is_selectable && setSelectedSeat(isSelected ? null : { ...seat, status, reservation: res })}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', cursor: seat.is_selectable ? 'pointer' : 'default' }}
              >
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  border: isSelected ? `2px solid ${primaryColor}` : `1px solid ${color}50`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isSelected ? `${primaryColor}20` : 'transparent',
                  boxShadow: isSelected ? `0 0 10px ${primaryColor}50` : 'none',
                  transition: 'all 0.15s'
                }}>
                  <MdOutlineDirectionsBike size={14} color={isSelected ? primaryColor : color} />
                </div>
                {seat.is_selectable && (
                  <span style={{ color: '#2d3748', fontSize: '9px', fontWeight: '600' }}>{seat.seat_number}</span>
                )}
              </div>
            );
          };

          return (
            <>
              {/* Fila instructor — igual que SeatMap */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
                {leftSeats.length > 0 && (
                  <div style={{ display: 'flex', gap: '3px' }}>{leftSeats.map(renderBike)}</div>
                )}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ height: '1px', background: `linear-gradient(90deg, transparent, ${primaryColor}, transparent)`, opacity: 0.4, width: '60px', margin: '0 auto 6px' }} />
                  <span style={{ color: '#334155', fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>▲ Tarima</span>
                </div>
                {rightSeats.length > 0 && (
                  <div style={{ display: 'flex', gap: '3px' }}>{rightSeats.map(renderBike)}</div>
                )}
              </div>

              {/* Resto de filas */}
              {Object.entries(seatsByRow).sort((a, b) => Number(a[0]) - Number(b[0])).map(([rowNum, rowSeats]) => {
                if (rowNum === '1') return null;
                return (
                  <div key={rowNum} style={{ display: 'flex', justifyContent: 'center', gap: '3px', marginBottom: '4px', flexWrap: 'nowrap' }}>
                    {rowSeats.map(renderBike)}
                  </div>
                );
              })}
            </>
          );
        })()}
      </div>

      {/* Detalle del asiento seleccionado */}
      {selectedSeat && (
        <div style={{
          borderRadius: '12px', padding: '13px', marginBottom: '12px',
          border: `1px solid ${selectedSeat.status === 'ocupada' ? 'rgba(239,68,68,0.3)'
            : selectedSeat.status === 'reservada' ? 'rgba(251,191,36,0.3)'
              : `${primaryColor}30`}`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h3 style={{ color: 'white', fontSize: '14px', fontWeight: '700', margin: 0 }}>
              Bici #{selectedSeat.seat_number}
            </h3>
            <span style={{
              fontSize: '10px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px',
              border: `1px solid ${selectedSeat.status === 'ocupada' ? 'rgba(239,68,68,0.4)'
                : selectedSeat.status === 'reservada' ? 'rgba(251,191,36,0.4)'
                  : 'rgba(74,222,128,0.4)'}`,
              color: selectedSeat.status === 'ocupada' ? '#ef4444'
                : selectedSeat.status === 'reservada' ? '#fbbf24' : '#4ade80',
              textTransform: 'uppercase'
            }}>
              {selectedSeat.status === 'disponible' ? 'Disponible'
                : selectedSeat.status === 'reservada' ? 'Pendiente' : 'Ocupado'}
            </span>
          </div>
          {selectedSeat.reservation ? (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <MiniChip label="Nombre" value={selectedSeat.reservation.customer_name} />
              <MiniChip label="Teléfono" value={selectedSeat.reservation.customer_phone} />
            </div>
          ) : (
            <p style={{ color: '#4a5568', fontSize: '12px', margin: 0 }}>Asiento disponible</p>
          )}
        </div>
      )}

      {/* Notas por fila */}
      {(() => {
        const session = localConfig?.sessions?.find(s => s.id === activeTab);
        const rowEntries = Object.entries(seatsByRow).sort((a, b) => Number(a[0]) - Number(b[0]));
        if (rowEntries.length === 0) return null;
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <h3 style={{ color: 'white', fontSize: '13px', fontWeight: '700', margin: 0 }}>Notas por Fila</h3>
              <span style={{ color: '#475569', fontSize: '11px' }}>Visibles al seleccionar bici</span>
            </div>
            {rowEntries.map(([rowNum, _], idx) => {
              const currentNote = (session?.rowNotes || [])[idx] || '';
              const isEditing = editingRowNote?.rowIndex === idx;
              return (
                <div key={rowNum} style={{ marginBottom: '8px', borderRadius: '10px', border: `1px solid ${currentNote ? `${primaryColor}25` : 'rgba(255,255,255,0.06)'}`, padding: '10px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isEditing ? '8px' : currentNote ? '6px' : '0' }}>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: idx === 0 ? primaryColor : '#94a3b8' }}>
                      {idx === 0 ? 'Fila 1 ★ (Instructor)' : `Fila ${idx + 1}`}
                    </span>
                    {!isEditing && (
                      <button
                        onClick={() => setEditingRowNote({ rowIndex: idx, note: currentNote })}
                        style={{
                          background: `${primaryColor}18`, color: primaryColor,
                          border: `1px solid ${primaryColor}30`, borderRadius: '6px',
                          padding: '3px 9px', fontSize: '11px', fontWeight: '700', cursor: 'pointer'
                        }}
                      >{currentNote ? 'Editar' : '+ Nota'}</button>
                    )}
                  </div>

                  {isEditing ? (
                    <div>
                      <textarea
                        autoFocus
                        value={editingRowNote.note}
                        onChange={e => setEditingRowNote({ ...editingRowNote, note: e.target.value })}
                        placeholder="Ej: Incluye termo, llavero y ropa deportiva"
                        rows={2}
                        style={{
                          width: '100%', padding: '8px 10px', borderRadius: '8px', boxSizing: 'border-box',
                          border: `1.5px solid ${primaryColor}40`, background: `${primaryColor}08`,
                          color: '#e2e8f0', fontSize: '13px', fontFamily: 'Inter, sans-serif',
                          resize: 'none', outline: 'none'
                        }}
                      />
                      <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                        <button
                          onClick={() => setEditingRowNote(null)}
                          style={{ flex: 1, padding: '7px', borderRadius: '7px', border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: '#64748b', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                        >Cancelar</button>
                        <button
                          onClick={() => handleSaveRowNote(editingRowNote.rowIndex, editingRowNote.note)}
                          disabled={savingNote}
                          style={{ flex: 2, padding: '7px', borderRadius: '7px', border: 'none', background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`, color: backgroundColor, fontSize: '12px', fontWeight: '800', cursor: 'pointer' }}
                        >{savingNote ? 'Guardando...' : 'Guardar'}</button>
                      </div>
                    </div>
                  ) : currentNote ? (
                    <p style={{ color: '#64748b', fontSize: '12px', margin: 0, lineHeight: 1.5, fontStyle: 'italic' }}>"{currentNote}"</p>
                  ) : null}
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}

function MiniChip({ label, value }) {
  return (
    <div style={{
      border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px',
      padding: '6px 10px', flex: 1, minWidth: '120px'
    }}>
      <p style={{ color: '#334155', fontSize: '10px', fontWeight: '600', margin: '0 0 2px 0', textTransform: 'uppercase' }}>{label}</p>
      <p style={{ color: 'white', fontSize: '12px', fontWeight: '600', margin: 0, wordBreak: 'break-word' }}>{value}</p>
    </div>
  );
}

export default AdminBikes;
